import { OrderSearch } from "orderManagement/models";
import apiClient from "./apiClient";
import { toSearchRequest } from "./ordersService";

// Bulk Event Reset history (OrderApi dbo.EventResetBatch / EventResetBatchTrip) and a trip's reset
// status. Times are UTC ISO strings ("...Z").

export type EventResetBatchStatus = "Queued" | "Running" | "Completed" | "Cancelled" | "Failed";
export type EventResetTripStatus = "Queued" | "Running" | "Succeeded" | "Skipped" | "Failed";
export type EventResetSource = "Manual" | "Scheduled";

export interface EventResetBatchSummary {
  batchId: string;
  source: EventResetSource;
  requestedByUserId: number;
  requestedBy?: string | null;
  requestedAt: string;
  startedAt?: string | null;
  finishedAt?: string | null;
  status: EventResetBatchStatus;
  tripCount: number;
  pendingCount: number;
  succeededCount: number;
  skippedCount: number;
  failedCount: number;
  approvedCount: number;
  notes?: string | null;
}

export interface EventResetBatchTripRow {
  tripId: number;
  clientName?: string | null;
  controlNumber?: string | null;
  pickupDateTime?: string | null;
  status: EventResetTripStatus;
  reason?: string | null;
  autoApproved?: boolean | null;
  autoApproveFailReason?: string | null;
  deactivatedEventCount?: number | null;
  acceptedSuggestionCount?: number | null;
  startedAt?: string | null;
  finishedAt?: string | null;
}

export interface EventResetBatchDetail {
  batch: EventResetBatchSummary;
  trips: EventResetBatchTripRow[];
}

export interface EventResetBatchPage {
  count: number;
  more: boolean;
  results: EventResetBatchSummary[];
}

export interface EventResetBatchQuery {
  from?: string | null;
  to?: string | null;
  status?: string | null;
  source?: string | null;
  userId?: number | null;
  clientId?: number | null;
  tripId?: number | null;
  offset?: number;
  pageSize?: number;
}

// "Queued"/"Running" while a bulk reset holds the trip (editing is refused), otherwise null.
export interface TripEventResetStatus {
  tripId: number;
  state?: "Queued" | "Running" | null;
  queuedBy?: string | null;
  queuedAt?: string | null;
  lastResetAt?: string | null;
  lastResetBy?: string | null;
  lastResetBatchId?: string | null;
  lastBatchId?: string | null;
  lastBatchTripStatus?: EventResetTripStatus | null;
  lastReason?: string | null;
  lastAutoApproved?: boolean | null;
  lastAutoApproveFailReason?: string | null;
  lastFinishedAt?: string | null;
}

// Drops empty filters so the BFF passes only real ones to OrderApi.
export const toQueryParams = (query: EventResetBatchQuery): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") params[key] = value as string | number;
  });
  return params;
};

const getBatches = async (query: EventResetBatchQuery): Promise<EventResetBatchPage> =>
  apiClient.get(`/events/resets/batches`, toQueryParams(query));

const getBatch = async (batchId: string): Promise<EventResetBatchDetail> => apiClient.get(`/events/resets/batches/${batchId}`);

const getTripStatus = async (tripId: number): Promise<TripEventResetStatus> => apiClient.get(`/events/resets/trip/${tripId}`);

// A trip left out of a queued submission (an eligibility rule), with its plain-English reason.
export interface QueuedBulkResetSkippedTrip {
  tripId: number;
  rules: string[];
  reason?: string | null;
}

// 202 answer to a queued bulk reset: the run is recorded and on the queue; the server does the trips.
export interface QueuedBulkResetResponse {
  batchId: string;
  queued: number;
  skipped: QueuedBulkResetSkippedTrip[];
}

// The whole selection as one queued submission. Rejects with 409 { queueUnavailable: true } where
// OrderApi has no queue configured (the caller then falls back to the chunked, synchronous path).
const submitQueuedBulkReset = async (tripIds: number[]): Promise<QueuedBulkResetResponse> =>
  apiClient.post(`/events/bulk-reset`, { tripIds, useQueue: true });

// Cancel a queued/running run: trips not started yet are skipped and released; a running one finishes.
const cancelBatch = async (batchId: string): Promise<{ message?: string }> =>
  apiClient.post(`/events/resets/batches/${batchId}/cancel`, {});

// The trips matching a search that a bulk reset would take (at most `max`, in the list's order) and
// how many were left out, by reason. Admin only: 403 { error } otherwise.
export interface EligibleTripsResponse {
  tripIds: number[];
  examined: number;
  skipped: { reason: string; count: number }[];
}

// The search goes as searchOrders sends it, from the first row: a select-all starts at the top.
const getEligibleTrips = async (search: OrderSearch, max: number): Promise<EligibleTripsResponse> =>
  apiClient.post(`/events/bulk-reset/eligible-trips`, {
    search: toSearchRequest({ ...search, pagedSearchOptions: { ...search.pagedSearchOptions, offset: 0 } }),
    max,
  });

const EventResetsService = {
  getBatches,
  getBatch,
  getTripStatus,
  submitQueuedBulkReset,
  cancelBatch,
  getEligibleTrips,
};

export type EventResetsApi = typeof EventResetsService;

export default EventResetsService;
